/**
 * Clinical Note RTF helpers.
 *
 * Reuses the Page Designer sanitizer (`sanitizeRtfHtml`) and the existing
 * contentEditable / execCommand architecture. NoteVersion.content.text stays
 * a string: historical notes are plain text; new drafts may store sanitized HTML.
 * No schema migration.
 */
import { sanitizeRtfHtml } from "@/lib/pageDesignerRtf";
import { plainTextFromContent, countWordsAndChars } from "@/lib/smartPhraseApi";

const RTF_TAG_RE = /<\s*(p|div|br|b|strong|i|em|u|ul|ol|li|span|font)\b/i;

export function looksLikeNoteRtf(value) {
  return RTF_TAG_RE.test(String(value || ""));
}

function escapeText(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Convert stored plain text into editor HTML without rewriting the source. */
export function plaintextToEditorHtml(text) {
  if (text == null || text === "") return "";
  return escapeText(text).replace(/\r\n/g, "\n").replace(/\n/g, "<br>");
}

/** Load either historical plain text or stored RTF into the editor surface. */
export function noteContentToEditorHtml(value) {
  if (typeof value !== "string" || !value) return "";
  if (looksLikeNoteRtf(value)) return sanitizeRtfHtml(value) || plaintextToEditorHtml(value);
  return plaintextToEditorHtml(value);
}

export function noteRtfPlainText(value) {
  if (!value) return "";
  if (looksLikeNoteRtf(value)) return plainTextFromContent(value, "richtext");
  return String(value);
}

export function noteTextHasContent(value) {
  return noteRtfPlainText(value).trim().length > 0;
}

/** Persist sanitized HTML, or "" when the editor is visually empty. */
export function serializeNoteRtf(html) {
  const clean = sanitizeRtfHtml(html);
  if (!noteTextHasContent(clean)) return "";
  return clean;
}

export function countNoteText(value) {
  const format = looksLikeNoteRtf(value) ? "richtext" : "plaintext";
  return countWordsAndChars(value || "", format);
}

const SHORTCUT_BLOCK_TAGS = new Set([
  "DIV",
  "P",
  "LI",
  "PRE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "TD",
  "TH",
  "BLOCKQUOTE",
]);

function isShortcutBlock(node) {
  return Boolean(node && node.nodeType === 1 && SHORTCUT_BLOCK_TAGS.has(node.tagName));
}

function isShortcutLineBreak(node) {
  return Boolean(node && node.nodeType === 1 && (node.tagName === "BR" || isShortcutBlock(node)));
}

export function textBeforeCursor(editor) {
  if (!editor || typeof window === "undefined") return "";
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return "";
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer) && range.commonAncestorContainer !== editor) {
    return "";
  }
  const pre = document.createRange();
  pre.selectNodeContents(editor);
  pre.setEnd(range.startContainer, range.startOffset);
  return pre.toString();
}

function textFromNodeBeforeOffset(node, offset) {
  if (!node) return "";
  if (node.nodeType === 3) return String(node.nodeValue || "").slice(0, offset);
  if (node.nodeType !== 1) return "";
  let text = "";
  const limit = Math.min(offset, node.childNodes.length);
  for (let i = 0; i < limit; i += 1) {
    const child = node.childNodes[i];
    if (isShortcutLineBreak(child)) {
      text = "";
      continue;
    }
    text += child.textContent || "";
  }
  return text;
}

/**
 * Shortcut detection must stay on the current visual line.
 * contentEditable Range.toString() concatenates adjacent blocks/BR-separated
 * lines without a newline, which would turn a new-line `.HPI` into `paragraph.HPI`.
 */
export function textBeforeCursorForShortcut(editor) {
  if (!editor || typeof window === "undefined") return "";
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return "";
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer) && range.commonAncestorContainer !== editor) {
    return "";
  }

  let text = textFromNodeBeforeOffset(range.startContainer, range.startOffset);
  let node = range.startContainer;
  while (node && node !== editor) {
    let prev = node.previousSibling;
    while (prev) {
      if (isShortcutLineBreak(prev)) return text;
      text = `${prev.textContent || ""}${text}`;
      prev = prev.previousSibling;
    }
    const parent = node.parentNode;
    if (!parent || parent === editor) return text;
    if (isShortcutBlock(parent)) return text;
    node = parent;
  }
  return text;
}

function lastTextNode(node) {
  if (!node) return null;
  if (node.nodeType === 3) return node;
  for (let i = node.childNodes.length - 1; i >= 0; i -= 1) {
    const found = lastTextNode(node.childNodes[i]);
    if (found) return found;
  }
  return null;
}

export function deleteCharsBeforeCursor(editor, count) {
  const n = Number(count);
  if (!editor || !Number.isFinite(n) || n <= 0) return;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);

  const deleteFromTextNode = (textNode, endOffset) => {
    if (!textNode || textNode.nodeType !== 3) return false;
    const end = Number(endOffset);
    if (!Number.isFinite(end) || end < n) return false;
    const del = document.createRange();
    del.setStart(textNode, end - n);
    del.setEnd(textNode, end);
    del.deleteContents();
    sel.removeAllRanges();
    sel.addRange(del);
    sel.collapseToEnd();
    return true;
  };

  const node = range.startContainer;
  const offset = range.startOffset;
  if (node.nodeType === 3 && deleteFromTextNode(node, offset)) return;

  if (node.nodeType === 1 && offset > 0) {
    const prev = node.childNodes[offset - 1];
    const textNode = lastTextNode(prev);
    if (textNode && deleteFromTextNode(textNode, (textNode.nodeValue || "").length)) return;
  }

  if (typeof sel.modify === "function") {
    sel.collapseToStart();
    for (let i = 0; i < n; i += 1) {
      sel.modify("extend", "backward", "character");
    }
    document.execCommand("delete");
  }
}

export function insertHtmlAtCursor(editor, html) {
  if (!editor) return;
  editor.focus();
  const snippet = String(html || "");
  const ok = document.execCommand("insertHTML", false, snippet);
  if (ok) return;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) {
    editor.innerHTML = `${editor.innerHTML || ""}${snippet}`;
    return;
  }
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const temp = document.createElement("div");
  temp.innerHTML = snippet;
  const frag = document.createDocumentFragment();
  let last = null;
  while (temp.firstChild) {
    last = temp.firstChild;
    frag.appendChild(last);
  }
  range.insertNode(frag);
  if (last) {
    range.setStartAfter(last);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

const INNER_BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "PRE",
  "BLOCKQUOTE",
  "UL",
  "OL",
]);

function hasInnerBlock(el) {
  return Array.from(el.children).some((child) => INNER_BLOCK_TAGS.has(child.tagName));
}

function collectPrintBlocks(node, blocks) {
  if (node.nodeType === 3) {
    const text = node.nodeValue || "";
    if (text.trim()) blocks.push(escapeText(text));
    return;
  }
  if (node.nodeType !== 1) return;
  const tag = node.tagName;
  if (tag === "BR") {
    blocks.push("<br>");
    return;
  }
  if (tag === "UL" || tag === "OL") {
    node.querySelectorAll(":scope > li").forEach((li) => blocks.push(li.outerHTML));
    return;
  }
  if (hasInnerBlock(node)) {
    Array.from(node.childNodes).forEach((child) => collectPrintBlocks(child, blocks));
    return;
  }
  if (node.outerHTML) blocks.push(node.outerHTML);
}

function rtfPrintBlocks(html) {
  const clean = sanitizeRtfHtml(html);
  if (!clean) return [""];
  if (typeof document === "undefined") return [clean];
  const root = document.createElement("div");
  root.innerHTML = clean;
  const blocks = [];
  Array.from(root.childNodes).forEach((child) => collectPrintBlocks(child, blocks));
  return blocks.length ? blocks : [clean];
}

function splitHtmlBlockByLines(block, lines, take, offset) {
  const open = String(block || "").match(/^<([a-z0-9]+)(\s[^>]*)?>/i);
  const tag = open ? open[1] : "div";
  const attrs = open && open[2] ? open[2] : "";
  const slice = lines
    .slice(offset, offset + take)
    .map((line) => escapeText(line))
    .join("<br>");
  return `<${tag}${attrs}>${slice}</${tag}>`;
}

function defaultWrapLines(text, columns) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const wrapped = [];
  for (const line of lines) {
    if (line.length <= columns) {
      wrapped.push(line);
      continue;
    }
    let rest = line;
    while (rest.length > columns) {
      let cut = rest.lastIndexOf(" ", columns);
      if (cut < Math.floor(columns * 0.5)) cut = columns;
      wrapped.push(rest.slice(0, cut));
      rest = rest.slice(cut).replace(/^\s+/, "");
    }
    if (rest.length) wrapped.push(rest);
  }
  return wrapped;
}

export function paginateNoteRtfBody(
  html,
  {
    firstPageLines = 24,
    nextPageLines = 36,
    columns = 92,
    wrapLines = defaultWrapLines,
    blockExtraLines = 0,
  } = {},
) {
  const blocks = rtfPrintBlocks(html);
  const pages = [];
  let current = [];
  let used = 0;
  let limit = firstPageLines;
  const extra = Number(blockExtraLines) > 0 ? Number(blockExtraLines) : 0;
  const costFor = (lineCount) => Math.max(1, lineCount) + extra;
  const flush = () => {
    if (!current.length) return;
    pages.push(current.join(""));
    current = [];
    used = 0;
    limit = nextPageLines;
  };
  const appendPiece = (markup, cost) => {
    current.push(markup);
    used += cost;
  };
  for (const block of blocks) {
    const lines = wrapLines(noteRtfPlainText(block) || " ", columns);
    const cost = costFor(lines.length);
    if (used + cost <= limit) {
      appendPiece(block, cost);
      continue;
    }
    if (current.length) flush();
    if (cost <= limit) {
      appendPiece(block, cost);
      continue;
    }
    let offset = 0;
    while (offset < lines.length) {
      if (current.length) flush();
      const take = Math.min(Math.max(1, Math.floor(limit - extra)), lines.length - offset);
      appendPiece(splitHtmlBlockByLines(block, lines, take, offset), costFor(take));
      offset += take;
    }
  }
  if (current.length) pages.push(current.join(""));
  return pages.length ? pages : [""];
}
